import OptimiserDefaultData from '@/data/optimiserDefaultData'
import to2DP from './to2DP'

/**
 * Calculates total cost of fast charging based on energy and time.
 *
 * @param {Object} options - An object containing the following properties:
 *   @param {number} [costPerKWH] - The cost of electricity per kWh, defaults to OptimiserDefaultData.kWhCostPublic.
 *   @param {number} [costPerMin] - The cost of electricity per minute, defaults to OptimiserDefaultData.electricityCostPerMinPublic.
 *   @param {number} energyCharged - The amount of energy charged in kWh.
 *   @param {number} timeCharged - The time taken to charge in minutes.
 * @return {number} The total cost of fast charging in 2 decimal places.
 * 
 * Assumes:
 * - energyCharged >= 0
 * - timeCharged >= 0
 * - cost values >= 0
 *
 * Negative inputs indicate upstream issues.
 */
export function calcFastChargingCost({
  costPerKWH = OptimiserDefaultData.kWhCostPublic,
  costPerMin = OptimiserDefaultData.electricityCostPerMinPublic,
  energyCharged,
  timeCharged,
}: {
  costPerKWH?: number
  costPerMin?: number
  energyCharged: number
  timeCharged: number
}) {
  return to2DP(costPerKWH * energyCharged + costPerMin * timeCharged)
}

/**
 * Calculates total cost of slow charging based on energy and time.
 * 
 * @param {Object} options - An object containing the following properties:
 *   @param {number} [costPerKWH] - The cost of electricity per kWh, defaults to OptimiserDefaultData.kWhCostHome.
 *   @param {number} energyCharged - The amount of energy charged in kWh.
 *   @param {number} timeCharged - The time taken to charge in minutes.
 * @return {number} The total cost of slow charging in 2 decimal places.
 * 
 * Assumes:
 * - energyCharged >= 0
 * - cost values >= 0
 *
 * Negative inputs indicate upstream issues.
 */
export function calcSlowChargingCost({
  costPerKWH = OptimiserDefaultData.kWhCostHome,
  energyCharged,
}: {
  costPerKWH?: number
  energyCharged: number
}) {
  return to2DP(costPerKWH * energyCharged)
}
